<template>
  <DraftMain
    :panelTitle="panelTitle"
    :initialValues="initialValues"
    :confirmDeletionFn="confirmDeletionFn"
    :closeModal="closeModal"
    :onSearch="onSearch"
    :showModal="showModal"
  >
    <template #searchHeadOperation>
      <div class="flex items-center justify-center gap-2"></div>
    </template>
    <template #searchPanel>
      <div class="flex flex-row flex-wrap w-full">
        <Field name="requestNumber" v-slot="{ field, errors: errs }">
          <SimpleTextInput
            :field="field"
            :errors="errs"
            :label="$t('requestNumber')"
            :maxLength="80"
            direction="flex-col"
            :isRequired="false"
            class="lg:w-1/2 flex-col px-2 w-full"
          />
        </Field>
        <Field
          v-if="financialDepositTypes.length"
          name="financialDepositTypes"
          v-slot="{ field, errors: errs, handleChange }"
          class="w-full"
        >
          <SimpleDropdown
            :field="field"
            :errors="errs"
            :label="$t('depositType')"
            :options="financialDepositTypes"
            :is-required="false"
            direction="flex-col"
            class="lg:w-1/2 flex-col px-2 w-full"
            @on-selection="
              (item) => {
                handleChange(+item?.id)
              }
            "
          ></SimpleDropdown>
        </Field>
        <Field name="unifiedNumber" v-slot="{ field, errors: errs }" v-if="!authStore.isUserEstablishment">
          <SimpleTextInput
            :field="field"
            :errors="errs"
            :label="$t('unifiedNumber')"
            :maxLength="12"
            direction="flex-col"
            :isRequired="false"
            class="lg:w-1/2 flex-col px-2 w-full"
          />
        </Field>
        <Field name="beneficiaryIdentityNumber" v-slot="{ field, errors: errs }" v-if="!authStore.isUserEstablishment">
          <SimpleTextInput
            :field="field"
            :errors="errs"
            :label="$t('identityNumber')"
            :maxLength="15"
            direction="flex-col"
            :isRequired="false"
            class="lg:w-1/2 flex-col px-2 w-full"
          />
        </Field>
      </div>
    </template>
    <template #searchResult>
      <DraftTable
        :is-show-lock-feature="false"
        :isLoading="isLoading"
        :tableData="tableData"
        :tableHeaders="tableHeaders"
        :initialValues="initialValues"
        :pageLength="pageLength"
        @onPageClick="onPageClick"
        @selected="selectedNumberOfRow"
        @checked="selectedRow"
        @checkOut="checkOut"
        @checkIn="checkIn"
        @delete="deleteSelected"
        class="overflow-x-auto"
      >
        <template #tableRow="{ request }">
          <td
            class="px-6 py-2 text-gray-700 text-sm font-normal border-r border-brand-gray-300 text-wrap"
          >
            {{ request.depositTypeId }}
          </td>
          <td
            class="px-6 py-2 text-gray-700 text-sm font-normal border-r border-brand-gray-300 text-wrap"
          >
            {{ request.requestNumber }}
            <div>{{ $t('totalPaidAmount') }} {{ request.paidAmount }} AED</div>
          </td>
          <td
            class="px-6 py-2 text-gray-700 text-sm font-normal border-r border-brand-gray-300 text-wrap"
          >
            {{ request.beneficiaryEnglishName }}
            <div>
              {{ request.beneficiaryArabicName }}
            </div>
          </td>
          <td
            class="px-6 py-2 text-gray-700 text-sm font-normal border-r border-brand-gray-300 text-wrap"
          >
            {{ request.paymentDate }}
          </td>
          >
        </template>

        <template #tableOperation>
          <button class="bg-primary size-6 rounded-md text-white cursor-pointer">
            <i class="fa fa-credit-card"></i>
          </button>
        </template>
      </DraftTable>
    </template>
  </DraftMain>
</template>

<script setup>
import { onBeforeMount, ref } from 'vue'
import { notify } from 'notiwind'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import { Field } from 'vee-validate'
import DraftMain from '@/views/individual/draft/Main.vue'
import DraftTable from '@/views/individual/draft/DraftTable.vue'
import apiHelperService from '@/services'
import SimpleDropdown from '@/components/SimpleForm/Dropdown.vue'
import SimpleTextInput from '@/components/SimpleForm/TextInput.vue'

import { useAuthStore } from '@/stores/auth'

import { useLookupsStore } from '@/stores/lookups'

const route = useRoute()
const router = useRouter()
const i18n = useI18n()

const authStore = useAuthStore()

const lookupsStore = useLookupsStore()

const draftId = ref([])
const showModal = ref(false)
const panelTitle = ref('financialDeposits')
const isLoading = ref(false)
const initialValues = ref({
  currentPage: 1,
  pageSize: 10
})

const pageLength = ref(10)
const tableData = ref([])
const financialDepositTypes = ref([])
const tableHeaders = ref([
  {
    value: 1,
    label: ''
  },
  {
    value: 2,
    label: i18n.t('Type')
  },
  {
    value: 3,
    label: i18n.t('requestNumber')
  },
  {
    value: 4,
    label: i18n.t('Beneficiary')
  },
  {
    value: 5,
    label: i18n.t('paymentdate')
  }
])

const requestTableData = async (value) => {
  isLoading.value = true
  const options = {
    ...value,
    currentPage: initialValues.value.currentPage,
    pageSize: pageLength.value
  }
  router.replace({ query: value })
  try {
    const response = await apiHelperService.post('/api/shared/getFinancialDeposits', options)
    if (response) {
      isLoading.value = false
      return response
    }
  } catch (error) {
    console.error('requestTableData', error)
  }
}

const selectedNumberOfRow = async (num) => {
  pageLength.value = num
  initialValues.value = {
    ...initialValues.value,
    pageSize: +pageLength.value,
    currentPage: 1
  }
  tableData.value = await requestTableData(initialValues.value)
}

const onPageClick = async (pageNum) => {
  initialValues.value = {
    ...initialValues.value,
    currentPage: +pageNum
  }
  tableData.value = await requestTableData(initialValues.value)
}

const onSearch = async (value) => {
  tableData.value = await requestTableData(value)
}

const selectedRow = (draft) => {
  if (!draft) return
  draftId.value = draft
}

const deleteSelected = (draft) => {
  draftId.value = draft
  showModal.value = true
}

const checkOut = async () => {
  tableData.value = await requestTableData(initialValues.value)
  notify(
    {
      group: 'success',
      title: i18n.t('Success'),
      text: i18n.t('checkedOutSuccessfully')
    },
    6000
  )
}

const checkIn = async () => {
  tableData.value = await requestTableData(initialValues.value)
  notify(
    {
      group: 'success',
      title: i18n.t('Success'),
      text: i18n.t('checkedInSuccessfully')
    },
    6000
  )
}

const confirmDeletionFn = async () => {
  try {
    const payload = draftId.value
    const response = await apiHelperService.post('/api/draft/delete', payload)
    if (response) {
      tableData.value = await requestTableData(initialValues.value)
      showModal.value = false
      notify(
        {
          group: 'success',
          title: i18n.t('Success'),
          text: i18n.t('DeletedSuccessfully')
        },
        6000
      )
    }
  } catch (error) {
    console.error('confirmDeletionFn', error)
  }
}

const setDraftResponse = async () => {
  tableData.value = await requestTableData(initialValues.value)
}

const fetchFinancialDepositTypes = async () => {
  const res = await lookupsStore.fetchLookup('/api/lookup/FinancialDepositType')
  financialDepositTypes.value = res.map((item) => {
    item.value = item.id
    item.code = item.id
    return item
  })
}

onBeforeMount(async () => {
  await fetchFinancialDepositTypes()
  await setDraftResponse()
  const searchParams = route.query
  initialValues.value = searchParams
})
</script>
<style></style>
